package com.essntl.uikit.components.input

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.*
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.interaction.InteractionSource
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.LocalTextSelectionColors
import androidx.compose.foundation.text.selection.TextSelectionColors
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.*
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.label.EsInputLabel
import com.essntl.uikit.components.input.message.EsInputMessage
import com.essntl.uikit.components.input.message.EsInputMessageState
import com.essntl.uikit.components.input.placeholder.EsInputPlaceholder
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.EsColorScheme
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState

@Composable
fun EsInput(
    value: String,
    onValueChange: (String) -> Unit,
    label: String? = null,
    infos: @Composable (() -> Unit)? = null,
    placeholder: String? = null,
    message: String? = null,
    prefix: @Composable (() -> Unit)? = null,
    suffix: @Composable (() -> Unit)? = null,
    showClearButton: Boolean = true,
    enabled: Boolean = true,
    focusable: Boolean = true,
    onClear: () -> Unit = {},
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = true,
    maxLines: Int? = null,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    minHeight: Dp = Dp.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    innerPaddingValues: PaddingValues = PaddingValues(),
    modifier: Modifier = Modifier,
) {
    // Holds the latest internal TextFieldValue state. We need to keep it to have the correct value
    // of the composition.
    var textFieldValueState by remember { mutableStateOf(TextFieldValue(text = value)) }
    // Holds the latest TextFieldValue that BasicTextField was recomposed with. We couldn't simply
    // pass `TextFieldValue(text = value)` to the CoreTextField because we need to preserve the
    // composition.
    val textFieldValue = textFieldValueState.copy(text = value)

    SideEffect {
        if (textFieldValue.selection != textFieldValueState.selection ||
            textFieldValue.composition != textFieldValueState.composition
        ) {
            textFieldValueState = textFieldValue
        }
    }
    // Last String value that either text field was recomposed with or updated in the onValueChange
    // callback. We keep track of it to prevent calling onValueChange(String) for same String when
    // CoreTextField's onValueChange is called multiple times without recomposition in between.
    var lastTextValue by remember(value) { mutableStateOf(value) }

    EsInput(
        value = textFieldValue,
        onValueChange = { newTextFieldValueState ->
            textFieldValueState = newTextFieldValueState

            val stringChangedSinceLastInvocation = lastTextValue != newTextFieldValueState.text
            lastTextValue = newTextFieldValueState.text

            if (stringChangedSinceLastInvocation) {
                onValueChange(newTextFieldValueState.text)
            }
        },
        label = label,
        infos = infos,
        placeholder = placeholder,
        message = message,
        prefix = prefix,
        suffix = suffix,
        showClearButton = showClearButton,
        onClear = onClear,
        enabled = enabled,
        readOnly = readOnly,
        focusable = focusable,
        state = state,
        visualTransformation = visualTransformation,
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        singleLine = singleLine,
        maxLines = maxLines ?: if (singleLine) 1 else Int.MAX_VALUE,
        minLines = minLines,
        interactionSource = interactionSource,
        cursorColor = cursorColor,
        minHeight = minHeight,
        shape = shape,
        innerPaddingValues = innerPaddingValues,
        modifier = modifier,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EsInput(
    value: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    label: String? = null,
    infos: @Composable (() -> Unit)? = null,
    placeholder: String? = null,
    message: String? = null,
    prefix: @Composable (() -> Unit)? = null,
    suffix: @Composable (() -> Unit)? = null,
    ignoreStateSuffixIcons: Boolean = false,
    showClearButton: Boolean = true,
    onClear: () -> Unit,
    enabled: Boolean = true,
    focusable: Boolean = true,
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = true,
    maxLines: Int?,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    minHeight: Dp = Dp.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    innerPaddingValues: PaddingValues = PaddingValues(),
    modifier: Modifier = Modifier,
) {
    val isError =
        remember(state) {
            state == EsInputState.Error
        }
    val isSuccess =
        remember(state) {
            state == EsInputState.Success
        }
    val isLoading =
        remember(state) {
            state == EsInputState.Loading
        }
    val focusRequester = remember { FocusRequester() }

    Column(
        modifier = modifier,
    ) {
        Box(
            modifier = Modifier.fillMaxWidth(),
        ) {
            if (label != null)
                EsInputLabel(
                    label = label,
                    modifier = Modifier.align(Alignment.CenterStart),
                )

            if (infos != null)
                Box(
                    modifier = Modifier.align(Alignment.CenterEnd),
                ) {
                    infos()
                }
        }

        if (infos != null || label != null)
            Spacer(
                modifier = Modifier.height(.5f.spacing),
            )

        val isHovered = interactionSource.collectIsHoveredAsState()
        val isFocused = interactionSource.collectIsFocusedAsState()

        val esColorScheme = MaterialTheme.esColorScheme
        val colors =
            remember(esColorScheme, isHovered.value, cursorColor) {
                getEsInputColors(
                    esColorScheme = esColorScheme,
                    hovered = isHovered.value,
                    cursorColor = cursorColor,
                )
            }
        val selectionColors =
            TextSelectionColors(
                handleColor = MaterialTheme.esColorScheme.textPrimary,
                backgroundColor = MaterialTheme.esColorScheme.primary.copy(.4f),
            )

        val textStyle = MaterialTheme.esTypography.p1Regular16

        // If color is not provided via the text style, use content color as a default
        val textColor =
            textStyle.color.takeOrElse {
                colors.textColor(enabled, isError, interactionSource).value
            }
        val mergedTextStyle = textStyle.merge(TextStyle(color = textColor))

        CompositionLocalProvider(LocalTextSelectionColors provides selectionColors) {
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                readOnly = readOnly,
                textStyle = mergedTextStyle,
                cursorBrush = SolidColor(colors.cursorColor(isError).value),
                visualTransformation = visualTransformation,
                keyboardOptions = keyboardOptions,
                keyboardActions = keyboardActions,
                interactionSource = interactionSource,
                singleLine = singleLine,
                maxLines = maxLines ?: if (singleLine) 1 else Int.MAX_VALUE,
                minLines = minLines,
                decorationBox = @Composable { innerTextField ->
                    OutlinedTextFieldDefaults.DecorationBox(
                        value = value.text,
                        visualTransformation = visualTransformation,
                        innerTextField = {
                            Box(
                                modifier = Modifier
                                    .padding(innerPaddingValues)
                                    .then(
                                        if (!focusable)
                                            Modifier.pointerInput(Unit) { // Disable pointer interaction
                                                detectTapGestures { /* No action on tap */ }
                                            }
                                                .focusProperties { canFocus = false }
                                        else
                                            Modifier,
                                    ),
                            )
                            {
                                innerTextField()
                            }
                        },
                        placeholder =
                            with(placeholder) {
                                when {
                                    this != null -> {
                                        {
                                            EsInputPlaceholder(
                                                this,
                                                modifier = Modifier.padding(innerPaddingValues),
                                            )
                                        }
                                    }

                                    else -> null
                                }
                            },
                        prefix =
                            when {
                                prefix != null -> {
                                    {
                                        Box(
                                            modifier =
                                                Modifier
                                                    .padding(end = 1.5f.spacing),
                                        ) {
                                            prefix()
                                        }
                                    }
                                }

                                else -> null
                            },
                        suffix =
                            when {
                                isLoading && !ignoreStateSuffixIcons -> {
                                    {
                                        EsSpinner(
                                            size = EsSpinnerSize.Large,
                                            color = MaterialTheme.esColorScheme.iconPrimary,
                                            modifier = Modifier,
                                        )
                                    }
                                }

                                isError && !isFocused.value && !ignoreStateSuffixIcons -> {
                                    {
                                        EsLargeIcon(
                                            res = EsIcons.InfoCircle,
                                            tint = MaterialTheme.esColorScheme.iconError,
                                        )
                                    }
                                }

                                suffix != null -> {
                                    {
                                        Box(
                                            modifier =
                                                Modifier
                                                    .padding(start = 1.5f.spacing),
                                        ) {
                                            suffix()
                                        }
                                    }
                                }

                                showClearButton && isFocused.value && enabled && !ignoreStateSuffixIcons -> {
                                    {
                                        EsLargeIcon(
                                            res = EsIcons.CloseCircle,
                                            tint = MaterialTheme.esColorScheme.iconDisabled,
                                            modifier =
                                                Modifier
                                                    .padding(start = 1.5f.spacing)
                                                    .clip(CircleShape)
                                                    .pointerHoverIcon(PointerIcon.Hand)
                                                    .clickable(
                                                        interactionSource = interactionSource,
                                                        indication = LocalIndication.current,
                                                    ) {
                                                        onValueChange(TextFieldValue())
                                                        focusRequester.requestFocus()
                                                        onClear()
                                                    },
                                        )
                                    }
                                }

                                else -> null
                            },
                        singleLine = singleLine,
                        enabled = enabled,
                        isError = isError,
                        interactionSource = interactionSource,
                        colors = colors.toTextFieldColors(),
                        contentPadding =
                            PaddingValues(
                                vertical = 1.spacing,
                                horizontal = 2.spacing,
                            ),
                        container = {
                            ContainerBox(
                                enabled = enabled,
                                isError = isError,
                                isSuccess = isSuccess,
                                interactionSource = interactionSource,
                                colors = colors,
                                shape = shape,
                                focusedBorderThickness = 1.dp,
                                unfocusedBorderThickness = 1.dp,
                            )
                        },
                    )
                },
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .heightIn(min = minHeight)
                        .focusRequester(focusRequester)
                        .hoverable(interactionSource = interactionSource)
                        .then(
                            if (!focusable)
                                Modifier
                                    .pointerInput(Unit) { detectTapGestures {} }
                                    .focusProperties { canFocus = false }
                            else
                                Modifier,
                        ),
            )
        }

        if (message != null) {
            Spacer(
                modifier = Modifier.height(.5f.spacing),
            )

            EsInputMessage(
                message = message,
                state = state.esInputMessageState,
            )
        }
    }
}

@Composable
internal fun ContainerBox(
    enabled: Boolean,
    isError: Boolean,
    isSuccess: Boolean,
    interactionSource: InteractionSource,
    colors: EsInputColors,
    shape: Shape,
    focusedBorderThickness: Dp,
    unfocusedBorderThickness: Dp,
) {
    val borderStroke =
        animateBorderStrokeAsState(
            enabled,
            isError,
            isSuccess,
            interactionSource,
            colors,
            focusedBorderThickness,
            unfocusedBorderThickness,
        )
    Box(
        Modifier
            .border(
                border = borderStroke.value,
                shape = shape,
            )
            .background(
                color = colors.containerColor(enabled, isError, interactionSource).value,
                shape = shape,
            ),
    )
}

@Composable
private fun animateBorderStrokeAsState(
    enabled: Boolean,
    isError: Boolean,
    isSuccess: Boolean,
    interactionSource: InteractionSource,
    colors: EsInputColors,
    focusedBorderThickness: Dp,
    unfocusedBorderThickness: Dp,
): State<BorderStroke> {
    val focused by interactionSource.collectIsFocusedAsState()
    val indicatorColor = colors.borderColor(enabled, isError, isSuccess, interactionSource)
    val targetThickness = if (focused) focusedBorderThickness else unfocusedBorderThickness
    val animatedThickness =
        if (enabled) {
            animateDpAsState(targetThickness, tween(durationMillis = AnimationDuration))
        } else {
            rememberUpdatedState(unfocusedBorderThickness)
        }
    return rememberUpdatedState(
        BorderStroke(animatedThickness.value, SolidColor(indicatorColor.value)),
    )
}

internal fun getEsInputColors(
    esColorScheme: EsColorScheme,
    hovered: Boolean = false,
    cursorColor: Color = Color.Unspecified,
) = with(esColorScheme) {
    EsInputColors(
        // TextColor
        focusedTextColor = textPrimary,
        unfocusedTextColor = textPrimary,
        disabledTextColor = textSecondary,
        errorTextColor = textPrimary,
        // ContainerColor
        focusedContainerColor = bgSecondary,
        unfocusedContainerColor = if (hovered) bgSecondaryHover else bgSecondary,
        disabledContainerColor = bgTable,
        errorContainerColor = if (hovered) bgSecondaryHover else bgSecondary,
        // CursorColor
        cursorColor = if (cursorColor.isSpecified) cursorColor else textPrimary,
        errorCursorColor = textPrimary,
        // SelectionColor
        textSelectionColors =
            TextSelectionColors(
                handleColor = textPrimary,
                backgroundColor = primary.copy(.4f),
            ),
        // BorderColor
        unfocusedBorderColor = borderPrimary,
        focusedBorderColor = borderAccent,
        disabledBorderColor = borderDisabled,
        errorBorderColor = borderError,
        successBorderColor = borderSuccess,
        // LeadingIcon
        unfocusedLeadingIconColor = iconSecondary,
        focusedLeadingIconColor = iconSecondary,
        disabledLeadingIconColor = iconSecondary,
        errorLeadingIconColor = iconSecondary,
        // TrailingIcon
        unfocusedTrailingIconColor = iconPrimary,
        focusedTrailingIconColor = iconPrimary,
        disabledTrailingIconColor = iconPrimary,
        errorTrailingIconColor = iconPrimary,
        // LabelColor
        unfocusedLabelColor = textPrimary,
        focusedLabelColor = textPrimary,
        disabledLabelColor = textPrimary,
        errorLabelColor = textPrimary,
        // PlaceholderColor
        unfocusedPlaceholderColor = textSecondary,
        focusedPlaceholderColor = textSecondary,
        disabledPlaceholderColor = textDisabled,
        errorPlaceholderColor = textSecondary,
        // SupportingTextColor
        unfocusedSupportingTextColor = textSecondary,
        focusedSupportingTextColor = textSecondary,
        disabledSupportingTextColor = textSecondary,
        errorSupportingTextColor = textSecondary,
        // PrefixColor
        unfocusedPrefixColor = textSecondary,
        focusedPrefixColor = textSecondary,
        disabledPrefixColor = textSecondary,
        errorPrefixColor = textSecondary,
        // SuffixColor
        unfocusedSuffixColor = textPrimary,
        focusedSuffixColor = textPrimary,
        disabledSuffixColor = textPrimary,
        errorSuffixColor = textPrimary,
    )
}

val EsInputState.esInputMessageState: EsInputMessageState
    get() =
        when (this) {
            EsInputState.Error -> EsInputMessageState.Error
            EsInputState.Success -> EsInputMessageState.Success
            else -> EsInputMessageState.Default
        }
